/**
 * ChainVault WP - Prediction Markets Page
 * Real betting with blockchain transactions and auto-resolution
 * @package ChainVault
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../context/WalletContext';
import { playSendSound } from '../utils/sounds';

const CATEGORIES = [ 'All', 'Crypto', 'Politics', 'Sports', 'Finance', 'Tech', 'Culture', 'Science' ];

/* ── Real Bet Modal ── */
function BetModal( { market, side, onClose, onBetPlaced } ) {
    const { address, builtInWallet, walletType, selectedNetwork, sendFromBuiltIn, balances } = useWallet();
    const [ amount,  setAmount  ] = useState( '' );
    const [ loading, setLoading ] = useState( false );
    const [ status,  setStatus  ] = useState( null );
    const [ step,    setStep    ] = useState( 'input' ); /* input | confirm | success */

    if ( !market ) return null;

    const isYes    = side === 'yes';
    const yes      = market.yes_percent || 50;
    const no       = 100 - yes;
    const yesLabel = market.yes_label || 'Yes';
    const noLabel  = market.no_label  || 'No';
    const network  = market.network   || 'Ethereum';
    const minBet   = parseFloat( market.min_bet ) || 0;
    const isSolana  = network.toLowerCase() === 'solana';
    const symbol    = isSolana ? 'SOL' : network === 'BSC' ? 'BNB' : network === 'Polygon' ? 'MATIC' : 'ETH';
    const myBalance = isSolana
        ? parseFloat( balances?.solana || 0 ).toFixed(4)
        : parseFloat( balances?.ethereum || 0 ).toFixed(4);
    const marketId  = market.id !== undefined ? market.id : market.index;

    /* get house wallet from settings */
    const settings      = window.chainvaultSettings || {};
    const houseWallet   = isSolana ? settings.predictionHouseSOL : settings.predictionHouseETH;
    const apiBase       = settings.siteUrl || '/wp-json/chainvault/v1';

    /* calculate potential winnings */
    const yesPool  = parseFloat( market.yes_pool  || 0 );
    const noPool   = parseFloat( market.no_pool   || 0 );
    const myAmount = parseFloat( amount ) || 0;
    const fee      = parseFloat( settings.predictionFee || 5 ) / 100;
    const newYes   = isYes ? yesPool + myAmount : yesPool;
    const newNo    = isYes ? noPool  : noPool + myAmount;
    const totalPool = newYes + newNo;
    const myShare   = isYes ? ( newYes > 0 ? myAmount / newYes : 0 ) : ( newNo > 0 ? myAmount / newNo : 0 );
    const potWin    = myAmount > 0 ? ( totalPool * ( 1 - fee ) * myShare ).toFixed(6) : '0';

    const handleBet = async () => {
        if ( !amount || parseFloat(amount) <= 0 ) {
            setStatus({ type: 'error', msg: 'Please enter a bet amount' });
            return;
        }
        if ( minBet > 0 && parseFloat(amount) < minBet ) {
            setStatus({ type: 'error', msg: 'Minimum bet is ' + minBet + ' ' + symbol });
            return;
        }
        if ( !walletType ) {
            setStatus({ type: 'error', msg: 'Please connect your wallet first' });
            return;
        }
        if ( !houseWallet ) {
            setStatus({ type: 'error', msg: 'House wallet not configured. Contact admin.' });
            return;
        }

        setLoading( true );
        setStatus( null );
        try {
            /* send real blockchain transaction to house wallet */
            const networkOverride = isSolana ? 'solana' : network.toLowerCase();
            const txHash = await sendFromBuiltIn( houseWallet, amount, networkOverride );
            playSendSound();

            /* record bet in WordPress */
            const userAddress = isSolana
                ? ( builtInWallet?.solana?.address || builtInWallet?.solana?.publicKey || address )
                : ( builtInWallet?.ethereum?.address || address );
            if ( !userAddress ) throw new Error('Could not get wallet address');

            const res = await fetch( apiBase + '/place-bet', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    market_id:    marketId,
                    user_address: userAddress,
                    side:         side,
                    amount:       parseFloat( amount ),
                    network:      network,
                    tx_hash:      txHash,
                }),
            });
            const data = await res.json();
            if ( data.success ) {
                setStep( 'success' );
                if ( onBetPlaced ) onBetPlaced( data );
            } else {
                throw new Error( data.message || 'Failed to record bet' );
            }
        } catch( e ) {
            setStatus({ type: 'error', msg: e.message || 'Transaction failed' });
        }
        setLoading( false );
    };

    return (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)' }} onClick={ onClose }>
            <div style={{ background:'#131313', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'20px', width:'90%', maxWidth:'420px', padding:'24px', display:'flex', flexDirection:'column', gap:'16px' }} onClick={ e => e.stopPropagation() }>

                { step === 'success' ? (
                    <>
                        <div style={{ textAlign:'center', padding:'20px 0' }}>
                            <div style={{ fontSize:'48px', marginBottom:'12px' }}>🎉</div>
                            <div style={{ fontSize:'20px', fontWeight:'800', color:'#00e5a0', marginBottom:'8px' }}>Bet Placed!</div>
                            <div style={{ fontSize:'13px', color:'#888', lineHeight:'1.5' }}>
                                You bet {amount} {symbol} on <strong style={{ color: isYes ? '#00e5a0' : '#ff5577' }}>{ isYes ? yesLabel : noLabel }</strong>.
                                If you win, you will receive approximately <strong style={{ color:'#fff' }}>{potWin} {symbol}</strong>.
                            </div>
                            <div style={{ fontSize:'11px', color:'#555', marginTop:'8px' }}>Check "My Bets" to track your position.</div>
                        </div>
                        <button onClick={ onClose } style={{ width:'100%', padding:'14px', borderRadius:'12px', border:'none', background:'#00e5a0', color:'#000', fontWeight:'700', fontSize:'15px', cursor:'pointer', fontFamily:'inherit' }}>Done</button>
                    </>
                ) : (
                    <>
                        { /* Header */ }
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                                <span style={{ fontSize:'24px' }}>{ isYes ? '✅' : '❌' }</span>
                                <div>
                                    <div style={{ fontSize:'11px', color:'#888', marginBottom:'2px' }}>Betting</div>
                                    <div style={{ fontWeight:'800', fontSize:'16px', color: isYes ? '#00e5a0' : '#ff5577' }}>{ isYes ? yesLabel.toUpperCase() : noLabel.toUpperCase() }</div>
                                </div>
                            </div>
                            <button onClick={ onClose } style={{ background:'none', border:'none', color:'#666', fontSize:'18px', cursor:'pointer' }}>✕</button>
                        </div>

                        { /* Question */ }
                        <div style={{ background:'rgba(255,255,255,0.04)', borderRadius:'10px', padding:'12px' }}>
                            <p style={{ color:'#fff', fontWeight:'600', fontSize:'13px', margin:'0 0 10px 0', lineHeight:'1.4' }}>{ market.question }</p>
                            <div style={{ display:'flex', height:'6px', borderRadius:'3px', overflow:'hidden', marginBottom:'5px' }}>
                                <div style={{ width: yes+'%', background:'#00e5a0' }} />
                                <div style={{ width: no +'%', background:'#ff5577' }} />
                            </div>
                            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', fontWeight:'700' }}>
                                <span style={{ color:'#00e5a0' }}>{ yesLabel } { yes }%</span>
                                <span style={{ color:'#ff5577' }}>{ no }% { noLabel }</span>
                            </div>
                        </div>

                        { /* Pool info */ }
                        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px' }}>
                            { [
                                { l:'Yes Pool', v: yesPool.toFixed(4) + ' ' + symbol },
                                { l:'No Pool',  v: noPool.toFixed(4)  + ' ' + symbol },
                                { l:'Platform Fee', v: (fee*100) + '%' },
                            ].map( (s,i) => (
                                <div key={i} style={{ background:'rgba(255,255,255,0.04)', borderRadius:'8px', padding:'10px', textAlign:'center' }}>
                                    <div style={{ fontSize:'10px', color:'#555', marginBottom:'3px', textTransform:'uppercase' }}>{ s.l }</div>
                                    <div style={{ fontSize:'12px', fontWeight:'700', color:'#ddd' }}>{ s.v }</div>
                                </div>
                            ))}
                        </div>

                        { /* Amount input */ }
                        <div>
                            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}><span style={{ fontSize:'12px', color:'#888', textTransform:'uppercase', letterSpacing:'0.5px' }}>Bet Amount ({ symbol })</span><span style={{ fontSize:'12px', color:'#555' }}>Balance: <strong style={{ color:'#ddd' }}>{ myBalance } { symbol }</strong></span></div>
                            <div style={{ position:'relative' }}>
                                <input
                                    type="number" min="0" step="any"
                                    value={ amount }
                                    onChange={ e => setAmount( e.target.value ) }
                                    placeholder={ minBet > 0 ? 'Min: ' + minBet : '0.00' }
                                    style={{ width:'100%', padding:'12px 60px 12px 14px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', color:'#fff', fontFamily:'inherit', fontSize:'16px', outline:'none', boxSizing:'border-box' }}
                                />
                                <span style={{ position:'absolute', right:'12px', top:'50%', transform:'translateY(-50%)', color:'#555', fontWeight:'700', fontSize:'13px' }}>{ symbol }</span>
                            </div>
                            { /* Quick amounts */ }
                            <div style={{ display:'flex', gap:'6px', marginTop:'8px' }}>
                                { ( isSolana ? ['0.01','0.05','0.1','0.5'] : ['0.001','0.005','0.01','0.05'] ).map( function(v) { return (
                                    <button key={v} onClick={ () => setAmount(v) }
                                        style={{ flex:1, padding:'6px 0', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'6px', color:'#888', cursor:'pointer', fontFamily:'inherit', fontSize:'11px' }}>
                                        { v }
                                    </button>
                                ); })}
                            </div>
                        </div>

                        { /* Potential winnings */ }
                        { myAmount > 0 && (
                            <div style={{ background: isYes ? 'rgba(0,229,160,0.08)' : 'rgba(255,85,119,0.08)', border: '1px solid ' + (isYes?'rgba(0,229,160,0.2)':'rgba(255,85,119,0.2)'), borderRadius:'10px', padding:'12px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                <span style={{ fontSize:'12px', color:'#888' }}>Potential winnings</span>
                                <span style={{ fontSize:'15px', fontWeight:'800', color: isYes ? '#00e5a0' : '#ff5577' }}>~{ potWin } { symbol }</span>
                            </div>
                        )}

                        { /* Status */ }
                        { status && (
                            <div style={{ padding:'10px 12px', borderRadius:'8px', background: status.type==='error'?'rgba(239,68,68,0.1)':'rgba(34,197,94,0.1)', border:'1px solid '+(status.type==='error'?'rgba(239,68,68,0.3)':'rgba(34,197,94,0.3)'), color: status.type==='error'?'#ef4444':'#22c55e', fontSize:'12px' }}>
                                { status.msg }
                            </div>
                        )}

                        { /* Bet button */ }
                        { !walletType ? (
                            <div style={{ padding:'12px', textAlign:'center', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'10px', color:'#555', fontSize:'13px' }}>
                                Connect your wallet to place a bet
                            </div>
                        ) : (
                            <button onClick={ handleBet } disabled={ loading || !amount }
                                style={{ width:'100%', padding:'14px', borderRadius:'12px', border:'none', cursor: loading||!amount?'not-allowed':'pointer', fontFamily:'inherit', fontSize:'15px', fontWeight:'700',
                                    background: loading||!amount ? 'rgba(255,255,255,0.05)' : isYes ? 'linear-gradient(135deg,#00e5a0,#00b87a)' : 'linear-gradient(135deg,#ff5577,#cc2244)',
                                    color: loading||!amount ? '#444' : '#fff', transition:'all 0.2s' }}>
                                { loading ? '⟳ Processing...' : 'Place Bet — ' + ( amount || '0' ) + ' ' + symbol }
                            </button>
                        )}

                        { /* Refund notice if opposite pool is empty */ }
                        { ( isYes ? noPool : yesPool ) === 0 && myAmount > 0 && (
                            <div style={{ background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.25)', borderRadius:'8px', padding:'10px 12px', fontSize:'11px', color:'#fbbf24', lineHeight:'1.6' }}>
                                ⚠️ <strong>Note:</strong> Nobody has bet on the opposite side yet. If this stays the same when the market resolves, you will receive a refund of your bet minus the { (fee*100) }% platform fee.
                            </div>
                        )}
                        <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'8px', padding:'10px 12px', fontSize:'11px', color:'#555', lineHeight:'1.7' }}>
                            <div style={{ fontWeight:'700', color:'#888', marginBottom:'4px' }}>📋 How payouts work:</div>
                            <div>• <span style={{ color:'#ddd' }}>Both sides bet → winners share losers pool proportionally</span></div>
                            <div>• <span style={{ color:'#ddd' }}>Only your side bets → you get back your amount minus { (fee*100) }% fee</span></div>
                            <div>• <span style={{ color:'#ddd' }}>Market cancelled → full refund minus { (fee*100) }% fee</span></div>
                            <div>• <span style={{ color:'#ddd' }}>Winnings sent to your wallet after resolution</span></div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/* ── My Bets Modal ── */
function MyBetsModal( { onClose } ) {
    const { address, builtInWallet, balances } = useWallet();
    const solBalance = parseFloat( balances?.solana || 0 );
    const fee        = window.chainvaultSettings?.predictionFee || 5;
    const [ bets,    setBets    ] = useState( [] );
    const [ loading, setLoading ] = useState( true );

    useEffect( () => {
        const solAddress = builtInWallet?.solana?.address || builtInWallet?.solana?.publicKey || '';
        const ethAddress = builtInWallet?.ethereum?.address || address || '';
        const apiBase    = ( window.chainvaultSettings?.siteUrl ) || '/wp-json/chainvault/v1';

        const fetchBets = async () => {
            setLoading( true );
            try {
                let allBets = [];
                if ( solAddress ) {
                    const res  = await fetch( apiBase + '/my-bets?address=' + solAddress );
                    const data = await res.json();
                    if ( Array.isArray(data) ) allBets = [ ...allBets, ...data ];
                }
                if ( ethAddress && ethAddress !== solAddress ) {
                    const res  = await fetch( apiBase + '/my-bets?address=' + ethAddress );
                    const data = await res.json();
                    if ( Array.isArray(data) ) allBets = [ ...allBets, ...data ];
                }
                setBets( allBets );
            } catch(e) {
                setBets( [] );
            }
            setLoading( false );
        };

        fetchBets();
    }, [ address, builtInWallet ] );

    const statusColors = { active:'#3b82f6', won:'#22c55e', lost:'#ef4444', refund:'#f59e0b', paid:'#8b5cf6' };

    return (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)' }} onClick={ onClose }>
            <div style={{ background:'#131313', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'20px', width:'90%', maxWidth:'500px', maxHeight:'80vh', display:'flex', flexDirection:'column' }} onClick={ e => e.stopPropagation() }>
                <div style={{ padding:'20px 24px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <span style={{ fontWeight:'800', fontSize:'16px', color:'#fff' }}>📋 My Bets</span>
                    <button onClick={ onClose } style={{ background:'none', border:'none', color:'#666', fontSize:'18px', cursor:'pointer' }}>✕</button>
                </div>
                <div style={{ overflowY:'auto', flex:1, padding:'16px' }}>
                    <div style={{ background:'rgba(108,99,255,0.08)', border:'1px solid rgba(108,99,255,0.2)', borderRadius:'10px', padding:'12px 16px', marginBottom:'12px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span style={{ fontSize:'12px', color:'#888' }}>Your SOL Balance</span>
                        <span style={{ fontSize:'15px', fontWeight:'800', color:'#a78bfa' }}>{ solBalance.toFixed(4) } SOL</span>
                    </div>
                    <div style={{ background:'rgba(245,158,11,0.08)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:'10px', padding:'12px 16px', marginBottom:'12px', fontSize:'12px', color:'#fbbf24', lineHeight:'1.6' }}>
                        <div style={{ fontWeight:'700', marginBottom:'4px' }}>📌 Important Notes</div>
                        <div>• Make sure your wallet has enough balance before placing bets</div>
                        <div>• When your bet shows <strong>WON 🏆</strong> — check your ChainVault wallet for the payout</div>
                        <div>• Winnings are sent to your wallet address after market resolution</div>
                        <div>• Platform fee of { fee }% is deducted from the total pool before payout</div>
                        <div>• <strong style={{color:'#fff'}}>If only YES bets exist and YES wins</strong> → you get your bet back minus { fee }% fee</div>
                        <div>• <strong style={{color:'#fff'}}>If only NO bets exist and NO wins</strong> → you get your bet back minus { fee }% fee</div>
                        <div>• <strong style={{color:'#fff'}}>If both sides bet and your side wins</strong> → you share the entire losing pool proportionally 🏆</div>
                        <div>• <strong style={{color:'#fff'}}>If market is cancelled</strong> → everyone gets refunded minus { fee }% fee</div>
                    </div>
                    { loading ? (
                        <div style={{ textAlign:'center', padding:'40px', color:'#555' }}>Loading...</div>
                    ) : bets.length === 0 ? (
                        <div style={{ textAlign:'center', padding:'40px', color:'#555' }}>No bets yet. Place your first bet!</div>
                    ) : bets.map( (bet,i) => (
                        <div key={i} style={{ background:'rgba(255,255,255,0.04)', borderRadius:'12px', padding:'16px', marginBottom:'10px', border:'1px solid rgba(255,255,255,0.06)' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
                                <span style={{ fontSize:'12px', fontWeight:'700', padding:'3px 10px', borderRadius:'6px', background: bet.side==='yes'?'rgba(0,229,160,0.15)':'rgba(255,85,119,0.15)', color: bet.side==='yes'?'#00e5a0':'#ff5577' }}>
                                    { bet.side==='yes' ? '✅ YES' : '❌ NO' }
                                </span>
                                <span style={{ fontSize:'11px', padding:'3px 10px', borderRadius:'6px', background:(statusColors[bet.status]||'#666')+'22', color:statusColors[bet.status]||'#666', fontWeight:'700' }}>
                                    { bet.status==='active' ? '🔵 ACTIVE' : bet.status==='won' ? '🏆 WON' : bet.status==='lost' ? '💔 LOST' : bet.status==='refund' ? '↩️ REFUND' : bet.status.toUpperCase() }
                                </span>
                            </div>
                            <div style={{ fontSize:'13px', color:'#ddd', marginBottom:'8px', fontWeight:'600', lineHeight:'1.4' }}>{ bet.market_question || 'Market #' + bet.market_id }</div>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', fontSize:'12px' }}>
                                <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:'6px', padding:'8px' }}>
                                    <div style={{ color:'#555', marginBottom:'2px' }}>Bet Amount</div>
                                    <div style={{ color:'#ddd', fontWeight:'700' }}>{ parseFloat(bet.amount).toFixed(6) } { bet.network }</div>
                                </div>
                                <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:'6px', padding:'8px' }}>
                                    <div style={{ color:'#555', marginBottom:'2px' }}>{ bet.status==='won' ? 'Payout' : 'Potential' }</div>
                                    <div style={{ color: bet.status==='won'?'#22c55e':'#888', fontWeight:'700' }}>
                                        { bet.payout_amount ? parseFloat(bet.payout_amount).toFixed(6) + ' ' + bet.network : '—' }
                                    </div>
                                </div>
                            </div>
                            { bet.market_resolved && (
                                <div style={{ marginTop:'8px', fontSize:'11px', padding:'6px 10px', borderRadius:'6px', background: bet.market_resolved==='yes'?'rgba(0,229,160,0.08)':'rgba(255,85,119,0.08)', color: bet.market_resolved==='yes'?'#00e5a0':'#ff5577' }}>
                                    Market resolved: { bet.market_resolved==='yes' ? '✅ YES won' : '❌ NO won' }
                                    { bet.side===bet.market_resolved ? ' — You won! 🏆' : ' — Better luck next time' }
                                </div>
                            )}
                            <div style={{ fontSize:'11px', color:'#444', marginTop:'6px' }}>Placed: { new Date(bet.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) }</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ── Custom Market Card ── */
function CustomMarketCard( { market, onBetPlaced } ) {
    const [ betSide,    setBetSide    ] = useState( null );
    const yes        = market.yes_percent || 50;
    const no         = 100 - yes;
    const isResolved = market.status === 'resolved';
    const isClosed   = market.status === 'closed';
    const yesPool    = parseFloat( market.yes_pool  || 0 );
    const noPool     = parseFloat( market.no_pool   || 0 );
    const totalPool  = yesPool + noPool;
    const settings   = window.chainvaultSettings || {};
    const symbol     = (market.network||'Ethereum').toLowerCase() === 'solana' ? 'SOL' :
                       (market.network||'') === 'BSC' ? 'BNB' :
                       (market.network||'') === 'Polygon' ? 'MATIC' : 'ETH';

    return (
        <div style={{ background:'#111', border:'1px solid rgba(252,114,255,0.15)', borderRadius:'12px', padding:'14px', display:'flex', flexDirection:'column', gap:'10px', transition:'border-color 0.2s' }}
            onMouseEnter={ e => e.currentTarget.style.borderColor='rgba(252,114,255,0.4)' }
            onMouseLeave={ e => e.currentTarget.style.borderColor='rgba(252,114,255,0.15)' }
        >
            <div style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                { market.image ? (
                    <img src={ market.image } alt="" style={{ width:'44px', height:'44px', borderRadius:'8px', objectFit:'cover', flexShrink:0 }} onError={ e => e.target.style.display='none' } />
                ) : (
                    <div style={{ width:'44px', height:'44px', borderRadius:'8px', background:'rgba(252,114,255,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px', flexShrink:0 }}>⭐</div>
                )}
                <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'4px', marginBottom:'4px', flexWrap:'wrap' }}>
                        <span style={{ fontSize:'10px', padding:'1px 6px', borderRadius:'4px', background:'rgba(252,114,255,0.15)', color:'#FC72FF', fontWeight:'700' }}>⭐ Custom</span>
                        { market.category && <span style={{ fontSize:'10px', padding:'1px 6px', borderRadius:'4px', background:'rgba(255,255,255,0.06)', color:'#888' }}>{ market.category }</span> }
                        { isResolved && <span style={{ fontSize:'10px', padding:'1px 6px', borderRadius:'4px', background:'rgba(0,229,160,0.1)', color:'#00e5a0', fontWeight:'700' }}>✅ Resolved</span> }
                        { isClosed   && <span style={{ fontSize:'10px', padding:'1px 6px', borderRadius:'4px', background:'rgba(255,255,255,0.06)', color:'#666' }}>Closed</span> }
                        { market.market_type === 'sports' && <span style={{ fontSize:'10px', padding:'1px 6px', borderRadius:'4px', background:'rgba(59,130,246,0.15)', color:'#60a5fa', fontWeight:'700' }}>⚽ Auto</span> }
                        { market.market_type === 'crypto' && <span style={{ fontSize:'10px', padding:'1px 6px', borderRadius:'4px', background:'rgba(245,158,11,0.15)', color:'#fbbf24', fontWeight:'700' }}>💰 Auto</span> }
                    </div>
                    <p style={{ fontWeight:'600', fontSize:'13px', color:'#fff', margin:0, lineHeight:'1.4', display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{ market.question }</p>
                </div>
            </div>

            <div>
                <div style={{ display:'flex', height:'4px', borderRadius:'2px', overflow:'hidden', marginBottom:'5px' }}>
                    <div style={{ width:yes+'%', background:'#00e5a0' }} />
                    <div style={{ width:no +'%', background:'#ff5577' }} />
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', fontWeight:'700' }}>
                    <span style={{ color:'#00e5a0' }}>{ market.yes_label||'Yes' } { yes }%</span>
                    <span style={{ color:'#ff5577' }}>{ no }% { market.no_label||'No' }</span>
                </div>
            </div>

            { /* Pool info */ }
            { totalPool > 0 && (
                <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:'#555', background:'rgba(255,255,255,0.03)', borderRadius:'6px', padding:'6px 10px' }}>
                    <span>Pool: { totalPool.toFixed(4) } { symbol }</span>
                    <span>{ market.bet_count || 0 } bets</span>
                </div>
            )}

            { isResolved ? (
                <div style={{ textAlign:'center', padding:'8px', borderRadius:'8px', background: market.resolved==='yes'?'rgba(0,229,160,0.1)':'rgba(255,85,119,0.1)', color: market.resolved==='yes'?'#00e5a0':'#ff5577', fontWeight:'700', fontSize:'13px' }}>
                    { market.resolved==='yes' ? '✅ YES Won' : '❌ NO Won' }
                    { market.resolved_note && <div style={{ fontSize:'11px', opacity:0.7, marginTop:'2px' }}>{ market.resolved_note }</div> }
                </div>
            ) : isClosed ? (
                <div style={{ textAlign:'center', padding:'8px', borderRadius:'8px', background:'rgba(255,255,255,0.04)', color:'#555', fontSize:'12px' }}>Market Closed — Awaiting Resolution</div>
            ) : (
                <div style={{ display:'flex', gap:'6px' }}>
                    <button onClick={ () => setBetSide('yes') }
                        style={{ flex:1, padding:'8px', borderRadius:'8px', border:'1px solid rgba(0,229,160,0.3)', background:'rgba(0,229,160,0.1)', color:'#00e5a0', fontWeight:'700', fontSize:'12px', cursor:'pointer', fontFamily:'inherit' }}>
                        ✅ { market.yes_label||'Yes' }
                    </button>
                    <button onClick={ () => setBetSide('no') }
                        style={{ flex:1, padding:'8px', borderRadius:'8px', border:'1px solid rgba(255,85,119,0.3)', background:'rgba(255,85,119,0.1)', color:'#ff5577', fontWeight:'700', fontSize:'12px', cursor:'pointer', fontFamily:'inherit' }}>
                        ❌ { market.no_label||'No' }
                    </button>
                </div>
            )}

            { betSide && (
                <BetModal
                    market={ market }
                    side={ betSide }
                    onClose={ () => setBetSide(null) }
                    onBetPlaced={ (data) => { setBetSide(null); if(onBetPlaced) onBetPlaced(data); } }
                />
            )}

            { market.end_date && (
                <div style={{ fontSize:'11px', color:'#444' }}>
                    Ends { new Date(market.end_date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) }
                    { market.min_bet && <span style={{ marginLeft:'8px' }}>Min: { market.min_bet }</span> }
                </div>
            )}
        </div>
    );
}

/* ── Live Market Card (Polymarket style) ── */
function PolyCard( { market, onClick } ) {
    const img     = market.featuredImage || market.image || null;
    const vol     = parseFloat( market.volume || 0 );
    const volStr  = vol >= 1e6 ? '$'+(vol/1e6).toFixed(1)+'M' : vol >= 1e3 ? '$'+(vol/1e3).toFixed(0)+'K' : '$'+vol.toFixed(0);
    const options = Array.isArray( market.options ) ? market.options : [];
    const isBin   = market.isBinary || options.length <= 1;
    const yes     = Math.round( market.outcomePrices ? JSON.parse(market.outcomePrices)[0]*100 : 50 );
    const no      = 100 - yes;

    return (
        <div style={{ background:'#111', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'14px', display:'flex', flexDirection:'column', gap:'10px', cursor:'pointer', transition:'border-color 0.2s' }}
            onMouseEnter={ e => e.currentTarget.style.borderColor='rgba(99,102,241,0.4)' }
            onMouseLeave={ e => e.currentTarget.style.borderColor='rgba(255,255,255,0.07)' }
            onClick={ () => onClick && onClick(market) }
        >
            <div style={{ display:'flex', gap:'10px', alignItems:'flex-start' }}>
                { img ? <img src={img} alt="" style={{ width:'44px', height:'44px', borderRadius:'8px', objectFit:'cover', flexShrink:0 }} onError={ e => e.target.style.display='none' } />
                      : <div style={{ width:'44px', height:'44px', borderRadius:'8px', background:'rgba(99,102,241,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px', flexShrink:0 }}>🔮</div> }
                <p style={{ fontWeight:'600', fontSize:'13px', color:'#fff', margin:0, lineHeight:'1.45', flex:1, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{ market.question }</p>
            </div>
            { isBin && (
                <div>
                    <div style={{ display:'flex', height:'4px', borderRadius:'2px', overflow:'hidden', marginBottom:'5px' }}>
                        <div style={{ width:yes+'%', background:'#00e5a0' }} />
                        <div style={{ width:no +'%', background:'#ff5577' }} />
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:'12px', fontWeight:'700' }}>
                        <span style={{ color:'#00e5a0' }}>Yes { yes }%</span>
                        <span style={{ color:'#ff5577' }}>{ no }% No</span>
                    </div>
                </div>
            )}
            { !isBin && (
                <div style={{ maxHeight:'100px', overflowY:'auto', display:'flex', flexDirection:'column', gap:'4px' }}>
                    { options.slice(0,4).map( (opt,i) => {
                        const pct = market.outcomePrices ? Math.round(JSON.parse(market.outcomePrices)[i]*100) : 0;
                        return (
                            <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                <span style={{ fontSize:'12px', color:'#ccc', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{ opt }</span>
                                <span style={{ fontSize:'12px', fontWeight:'700', color:'#818cf8', minWidth:'35px', textAlign:'right' }}>{ pct }%</span>
                            </div>
                        );
                    })}
                </div>
            )}
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', color:'#555' }}>
                <span>{ volStr } Vol.</span>
                { market.endDate && <span>Ends { new Date(market.endDate).toLocaleDateString() }</span> }
            </div>
            <div style={{ display:'flex', gap:'6px' }}>
                <button onClick={ e => { e.stopPropagation(); onClick && onClick(market,'yes'); } }
                    style={{ flex:1, padding:'7px', borderRadius:'7px', border:'1px solid rgba(0,229,160,0.3)', background:'rgba(0,229,160,0.1)', color:'#00e5a0', fontWeight:'700', fontSize:'12px', cursor:'pointer', fontFamily:'inherit' }}>
                    ✅ Yes { yes }%
                </button>
                <button onClick={ e => { e.stopPropagation(); onClick && onClick(market,'no'); } }
                    style={{ flex:1, padding:'7px', borderRadius:'7px', border:'1px solid rgba(255,85,119,0.3)', background:'rgba(255,85,119,0.1)', color:'#ff5577', fontWeight:'700', fontSize:'12px', cursor:'pointer', fontFamily:'inherit' }}>
                    ❌ No { no }%
                </button>
            </div>
        </div>
    );
}

/* ── Main Page ── */
export default function PredictionPage() {
    const settings          = window.chainvaultSettings || {};
    const adminMarkets      = Array.isArray( settings.predictionMarkets ) ? settings.predictionMarkets : [];
    const apiBase           = settings.siteUrl || '/wp-json/chainvault/v1';
    const browseEnabled     = settings.predictionBrowseEnabled || false;

    const [ tab,        setTab        ] = useState( 'custom' );
    const [ category,   setCategory   ] = useState( 'All' );
    const [ liveMarkets,setLiveMarkets] = useState( [] );
    const [ liveLoading,setLiveLoading] = useState( false );
    const [ liveModal,  setLiveModal  ] = useState( null );
    const [ markets,    setMarkets    ] = useState( [] );
    const [ myBets,     setMyBets     ] = useState( false );
    const [ refreshKey, setRefreshKey ] = useState( 0 );

    /* fetch live pool data from REST API */
    const fetchMarkets = useCallback( async () => {
        try {
            const res  = await fetch( apiBase + '/markets' );
            const data = await res.json();
            if ( Array.isArray(data) && data.length > 0 ) {
                setMarkets( data );
            } else {
                setMarkets( adminMarkets.map( (m,i) => ({...m, id: m.id !== undefined ? m.id : i}) ) );
            }
        } catch {
            setMarkets( adminMarkets.map( (m,i) => ({...m, id: m.id !== undefined ? m.id : i}) ) );
        }
    }, [ apiBase ] );

    useEffect( () => { fetchMarkets(); }, [ fetchMarkets, refreshKey ] );

    /* fetch live Polymarket data */
    useEffect( () => {
        if ( tab !== 'live' ) return;
        setLiveLoading( true );
        fetch( 'https://gamma-api.polymarket.com/markets?active=true&closed=false&limit=40&order=volume&ascending=false' )
            .then( r => r.json() )
            .then( data => {
                const items = Array.isArray(data) ? data : (data.markets || []);
                const filtered = items.filter( m => {
                    if (!m.question) return false;
                    const q = m.question.toLowerCase();
                    const fakeNames = ['team a','team b','candidate a','candidate b','player a','player b'];
                    return !fakeNames.some( n => q.includes(n) );
                });
                setLiveMarkets( filtered );
                setLiveLoading( false );
            })
            .catch( () => setLiveLoading(false) );
    }, [tab] );

    const filtered = markets.filter( m =>
        category === 'All' || (m.category||'').toLowerCase() === category.toLowerCase()
    );

    return (
        <div style={{ maxWidth:'1200px', margin:'0 auto', padding:'20px', fontFamily:'inherit' }}>

            { /* Header */ }
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', flexWrap:'wrap', gap:'10px' }}>
                <div>
                    <h1 style={{ color:'#fff', margin:'0 0 4px 0', fontSize:'24px', fontWeight:'800' }}>🔮 Prediction Markets</h1>
                    <p style={{ color:'#555', margin:0, fontSize:'13px' }}>Bet on crypto prices, sports and world events with real crypto</p>
                </div>
                <button onClick={ () => setMyBets(true) }
                    style={{ padding:'8px 16px', background:'rgba(252,114,255,0.1)', border:'1px solid rgba(252,114,255,0.3)', borderRadius:'8px', color:'#FC72FF', fontWeight:'700', fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>
                    📋 My Bets
                </button>
            </div>

            { /* Tabs */ }
            <div style={{ display:'flex', background:'rgba(255,255,255,0.04)', borderRadius:'10px', padding:'4px', gap:'4px', marginBottom:'16px', width:'fit-content' }}>
                { [['custom','🎯 Bet Now'], ...(browseEnabled ? [['live','🌐 Browse Markets']] : [])].map( ([t,l]) => (
                    <button key={t} onClick={ () => setTab(t) }
                        style={{ padding:'8px 18px', borderRadius:'7px', border:'none', background: tab===t?'rgba(255,255,255,0.1)':'transparent', color: tab===t?'#fff':'#555', fontWeight: tab===t?700:500, fontSize:'13px', cursor:'pointer', fontFamily:'inherit' }}>
                        { l }
                    </button>
                ))}
            </div>

            { tab === 'custom' && (
                <>
                    { /* Category filter */ }
                    <div style={{ display:'flex', gap:'6px', marginBottom:'16px', overflowX:'auto', paddingBottom:'4px' }}>
                        { CATEGORIES.map( c => (
                            <button key={c} onClick={ () => setCategory(c) }
                                style={{ padding:'5px 14px', borderRadius:'20px', border:'1px solid', borderColor: category===c?'#FC72FF':'rgba(255,255,255,0.1)', background: category===c?'rgba(252,114,255,0.15)':'transparent', color: category===c?'#FC72FF':'#555', fontWeight: category===c?700:400, fontSize:'12px', cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' }}>
                                { c }
                            </button>
                        ))}
                    </div>

                    { filtered.length === 0 ? (
                        <div style={{ textAlign:'center', padding:'60px 20px', color:'#555' }}>
                            <div style={{ fontSize:'40px', marginBottom:'12px' }}>🔮</div>
                            <p>No markets available yet. Admin can create markets in the WordPress admin panel.</p>
                        </div>
                    ) : (
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'14px' }}>
                            { filtered.map( (m,i) => (
                                <CustomMarketCard
                                    key={ i }
                                    market={ m }
                                    onBetPlaced={ () => { setTimeout( () => setRefreshKey(k=>k+1), 1000 ); } }
                                />
                            ))}
                        </div>
                    )}
                </>
            )}

            { tab === 'live' && (
                <>
                    <div style={{ background:'rgba(252,114,255,0.08)', border:'1px solid rgba(252,114,255,0.2)', borderRadius:'8px', padding:'12px 16px', marginBottom:'16px', fontSize:'13px', color:'#FC72FF' }}>💡 These are real prediction markets for reference. To bet on similar markets, ask your admin to import them via WordPress Admin → Prediction Markets → Import.</div>
                    { liveLoading ? (
                        <div style={{ textAlign:'center', padding:'60px', color:'#555' }}>
                            <div className="cv-market-spinner" style={{ margin:'0 auto 12px' }}></div>
                            Loading live markets...
                        </div>
                    ) : liveMarkets.length === 0 ? (
                        <div style={{ textAlign:'center', padding:'60px', color:'#555' }}>No live markets available</div>
                    ) : (
                        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'14px' }}>
                            { liveMarkets.map( (m,i) => (
                                <PolyCard key={i} market={m} onClick={ () => setLiveModal(m) } />
                            ))}
                        </div>
                    )}
                </>
            )}

            { /* Live market info modal */ }
            { liveModal && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:300, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)' }} onClick={ () => setLiveModal(null) }>
                    <div style={{ background:'#131313', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'20px', width:'90%', maxWidth:'440px', padding:'24px' }} onClick={ e => e.stopPropagation() }>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'16px' }}>
                            <span style={{ fontWeight:'800', color:'#fff', fontSize:'15px' }}>🌐 Live Market</span>
                            <button onClick={ () => setLiveModal(null) } style={{ background:'none', border:'none', color:'#666', fontSize:'18px', cursor:'pointer' }}>✕</button>
                        </div>
                        <p style={{ color:'#ddd', fontSize:'14px', lineHeight:'1.5', marginBottom:'16px' }}>{ liveModal.question }</p>
                        <div style={{ background:'rgba(252,114,255,0.08)', border:'1px solid rgba(252,114,255,0.2)', borderRadius:'10px', padding:'14px', textAlign:'center', marginBottom:'16px' }}>
                            <div style={{ fontSize:'13px', color:'#888' }}>Live betting on external markets is view-only.</div>
                            <div style={{ fontSize:'13px', color:'#888', marginTop:'4px' }}>Use Custom Markets tab to bet with real crypto.</div>
                        </div>
                        <button onClick={ () => setLiveModal(null) } style={{ width:'100%', padding:'12px', borderRadius:'10px', border:'none', background:'rgba(255,255,255,0.06)', color:'#ccc', cursor:'pointer', fontFamily:'inherit', fontWeight:'600' }}>Close</button>
                    </div>
                </div>
            )}

            { myBets && <MyBetsModal onClose={ () => setMyBets(false) } /> }
        </div>
    );
}
